/**
 * Prebid Bidder
 * Handles header bidding with Prebid
 * version 1.5.5
 * 
 * Using Semantic Versioning, please update accordingly to keep files in sync across code bases
 * 
 * Given a version number MAJOR.MINOR.PATCH, increment the:
 *  1. MAJOR version when you implement a full re-build of the architecture,
 *  2. MINOR version when you add functionality, and
 *  3. PATCH version when you implement bug fixes or make small  tweaks to current functionality.
 */
define('AdBidders/prebid',[
    "jquery",
    "logging",
    "utilities",
    "AdPlatforms/dfp"
], function ($, logging, utilities, dfp) {
    "use strict";

    var _ModuleName = "Prebid Header Bidding";

    var _config = {}
    var _bidderSettings = {
        standard: {
            alwaysUseBid: false,
            adserverTargeting: [
                {
                    key: "hb_bidder",
                    val: function (bidResponse) {
                        return bidResponse.bidderCode;
                    }
                }, {
                    key: "hb_adid",
                    val: function (bidResponse) {
                        return bidResponse.adId;
                    }
                }, {
                    key: "hb_pb",
                    val: function (bidResponse) {
                        var bid;
                        var cpm = bidResponse.cpm;
                        if (cpm < 0.05) {
                            bid = 0.00; // bids lower than $0.05 can be reduced to $0.00
                        } else if (cpm < 1.50) { // 0.05 -> 1.50
                            bid = (Math.floor(cpm * 20) / 20).toFixed(2); // 0.05 inc
                        } else if (cpm < 2.50) { // 1.50 -> 2.50
                            bid = (Math.floor(cpm * 10) / 10).toFixed(2); // 0.10 inc
                        } else if (cpm < 20.00) { // 2.50 -> 20.00
                            bid = (Math.floor(cpm * 2) / 2).toFixed(2); // 0.50 inc
                        } else {
                            bid = "20.00";
                        }

                        return bid;
                    }
                }, {
                    key: "hb_size",
                    val: function (bidResponse) {
                        return bidResponse.size;
                    }
                }
            ]
        }
    }
    var _data = {
        slots: [],
        failsafeEngaged: false,
        dfpMonitor: '',
        dfpMonitorSubscriptionName: 'prebid',
        ready: false
    }

    var _initialized = false;
    var init = function (config, monitorName) {
        $.extend(_config, config.bidders.prebid);

        // store dfp monitor name
        _data.dfpMonitor = monitorName;

        if (!_config.enabled) return;
        ;

        // Subscribe to Ready Monitor
        ;
        utilities.subscribeToReadyMonitor(_data.dfpMonitor, _data.dfpMonitorSubscriptionName);

        // intiialize global variables
        window.pbjs = window.pbjs || {};
        window.pbjs.que = window.pbjs.que || [];

        // Load Prebid.js
        ;
        _loadPrebidJs(_handlePrebidJsLoaded);

        ;
    }

    var start = function () {
        // if initialization is complete, run
        if (_initialized) run();
    }

    var _perfStart;
    var _running = false;
    var run = function () {
        if (_running) return;
        _running = true;

        if (_data.slots) {
            ;
            ;

            // get list of adapters
            var adapterModuleConfigs = _config.adapters
                .filter(function (adapter) { return adapter.enabled; });      // filtered for those that are currently enabled
            var adapterModules = adapterModuleConfigs
                .map(function (adapter) { return adapter.moduleName; });     // mapped to the module names
            if (adapterModules && adapterModules.length) {
                ;
                // load all adapter modules
                require(adapterModules, function () {
                    var adapters = $.makeArray(arguments); // Get List of Adapters Loaded
                    ;

                    // initialize adapter
                    for (var i = 0; i < adapters.length; i++) {
                        var config = adapterModuleConfigs[i];
                        var adapter = adapters[i];
                        ;
                        adapter.init(config);
                        ;
                        _bidderSettings[config.name] = adapter.getBidderSettings();
                    }

                    ;
                    window.pbjs.bidderSettings = _bidderSettings;

                    ;
                    var configs = _generateSlotConfigs(adapters);

                    if (Array.isArray(configs) && configs.length > 0) {
                        // reset our ready flag
                        _data.ready = false;

                        // reset adserver set flag on pbjs
                        window.pbjs.initAdserverSet = false;

                        _enqueue(function () {
                            _perfStart = new Date();
                            window.pbjs.setConfig(_config.pbjsConfig);
                            window.pbjs.addAdUnits(configs);
                            window.pbjs.requestBids({
                                bidsBackHandler: _bidsBackHandler,
                                timeout: _config.timeouts.bidding
                            });
                        });

                        setTimeout(function () {
                            if (!_data.ready) {
                                // failsafe skip bidding
                                ;
                                _bidsBackHandler();                                
                            }
                        }, _config.timeouts.failsafe);
                    } else {
                        ;
                        utilities.confirmReady(_data.dfpMonitor, _data.dfpMonitorSubscriptionName);
                        _running = false;
                    }
                });
            } else {
                logging.warn("No enabled prebid adapters", _ModuleName);
                utilities.confirmReady(_data.dfpMonitor, _data.dfpMonitorSubscriptionName);
                _running = false;
            }
        } else {
            ;
            utilities.confirmReady(_data.dfpMonitor, _data.dfpMonitorSubscriptionName);
            _running = false;
        }
    }

    /**
     * 
     * @param {Array<Object>} slots Array of slot definitions to register
     */
    var registerAdSlots = function (slots) {
        if (!_config.enabled) return;

        if (slots) {
            // filter slots for non OOP slots
            slots = slots.filter(function (slot) { return !slot.outofpage; });

            ;
            _data.slots = slots;
        } else {
            logging.warn("There was a problem registering ad slots. None were defined", _ModuleName);
        }
    }

    var refreshAdSlots = function (slots, monitor) {
        // make sure we've been initialized
        if (!_initialized) { return; }

        // store monitor name
        _data.dfpMonitor = monitor;

        // Subscribe to Ready Monitor if we're not already subscribed
        if (!utilities.isSubscribedToReadyMonitor(_data.dfpMonitor, _data.dfpMonitorSubscriptionName)) {
            utilities.subscribeToReadyMonitor(_data.dfpMonitor, _data.dfpMonitorSubscriptionName);
        }

        // register our new set of slots
        registerAdSlots(slots);

        // run bidding
        _data.ready = false;
        run();
    }

    /**
     * Removes slot from being tracked
     */
    var removeSlot = function (slot) {
        try {
            var index = _data.slots.findIndex(function (s) { return s.id == slot.id; });
            if (index >= 0) {
                ;
                _data.slots.splice(index, 1);
                ;
            }
        } catch (error) {
            logging.error("Failed to remove slot", _ModuleName);
            logging.error(error, _ModuleName);
        }
    }

    /**
     * Adds a callback function to the PBJS Queue
     * @param {function} callback Callback Function
     */
    var _enqueue = function (callback) {
        window.pbjs.que.push(callback);
    }

    /**
     * Load Prebid.js (Vendor Script)
     * @param {function} success function to run on loading success
     */
    var _loadPrebidJs = function (success) {
        if ($('script[src="' + _config.scriptUrl + '"]').length == 0) {
            $.ajax({
                url: _config.scriptUrl,
                dataType: "script",
                cache: true,
                success: function () {
                    ;

                    if (typeof success == 'function') {
                        ;
                        success();
                    }
                }
            }).fail(function () {
                logging.error("Something went wrong loading Prebid JS", _ModuleName);
            });
        } else {
            ;

            if (typeof success == 'function') {
                ;
                success();
            }
        }
    }

    var _handlePrebidJsLoaded = function () {
        _initialized = true;
        start();
    }

    /**
     * Generate all slot configurations for submission to prebid
     * @param {Array<Object>} adapters array of objects representing prebid adapters
     */
    var _generateSlotConfigs = function (adapters) {
        var slotConfigs = [];
        for (var i = 0; i < _data.slots.length; i++) {
            var slot = _data.slots[i];

            // filter out "Fluid" ad size
            var sizes = Array.isArray(slot.sizes) ? slot.sizes.filter(function (size) { return size != "fluid"; }) : [];

            // make sure our slot def is valid
            if (sizes.length == 0) {
                logging.warn('Slot ' + slot.position + ' does not have valid size definition. Excluding from PrebidJS.', _ModuleName);
                continue;
            }

            var slotBids = _getBidsForSlot(adapters, slot);
            if (Array.isArray(slotBids) && slotBids.length > 0) {
                var slotConfig = {
                    code: slot.id,
                    mediaTypes: {
                        banner: {
                            sizes: sizes
                        }
                    },
                    bids: slotBids
                };

                slotConfigs.push(slotConfig);
            }
        }

        return slotConfigs;
    }

    /**
     * Generates all bids for a given slot based on the adapters provided
     * @param {Array<Object>} adapters array of objects representing prebid adapters
     * @param {Object} slot Slot Configuration Object
     */
    var _getBidsForSlot = function (adapters, slot) {
        var bids = [];

        for (var i = 0; i < adapters.length; i++) {
            var adapter = adapters[i];
            ;
            var bidDef = adapter.getBidDefinition(slot);
            if (Array.isArray(bidDef) && bidDef.length > 0) {
                bids = bids.concat(bidDef);
            } else if (typeof bidDef !== 'undefined' && !Array.isArray(bidDef)) {
                bids.push(bidDef);
            } else {
                console.debug("unknown bid def, likely empty array");
            }
        }

        return bids;
    }

    /**
     * Handles setting targeting once the bids come back
     */
    var _bidsBackHandler = function () {
        if (_data.ready) { return; }

        // mark ready so this only runs once
        _data.ready = true;

        if (window.pbjs.initAdserverSet) return;
        window.pbjs.initAdserverSet = true;

        // if we engaged the failsafe, no extra work needed
        if (_data.failsafeEngaged) return;

        var now = new Date();
        var elapsed = now.getTime() - _perfStart.getTime();
        logging.log("PrebidJS bidding completed in " + elapsed + "ms", _ModuleName);

        dfp.enqueue(function () {
            ;
            pbjs.setTargetingForGPTAsync && pbjs.setTargetingForGPTAsync();

            // Confirm Ready with Ready Monitor
            ;
            utilities.confirmReady(_data.dfpMonitor, _data.dfpMonitorSubscriptionName);
            _running = false;
        });
    }

    return {
        enabled: (function () { return _config.enabled; }),
        init: init,
        start: start,
        registerAdSlots: registerAdSlots,
        refreshAdSlots: refreshAdSlots,
        removeSlot: removeSlot
    }
});
